import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { Save, RotateCcw, BarChart3, Search, Users, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function PerformancePolicyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "ADMIN";
  const [policy, setPolicy] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");

  useEffect(() => {
    api.get("/performance/policy").then(r => setPolicy(r.data)).catch(() => toast.error("Failed to load policy"));
    api.get("/users").then(r => setEmployees((r.data || []).filter((u: any) => u.isActive !== false && !u.isDeleted))).catch(() => {});
  }, []);

  const departments = Array.from(new Set(employees.map(e => e.department).filter(Boolean))).sort();
  const filteredEmployees = employees.filter(e => {
    const matchesSearch = !search || e.name?.toLowerCase().includes(search.toLowerCase());
    const matchesDept = !deptFilter || e.department === deptFilter;
    return matchesSearch && matchesDept;
  });

  if (!policy) return <div className="p-6 text-gray-500">Loading policy...</div>;

  const weightSum = Number(policy.wOutput) + Number(policy.wEffort) + Number(policy.wQuality) + Number(policy.wSpeed) + Number(policy.wDiscipline);

  const setWeight = (k: string, v: number) => setPolicy({ ...policy, [k]: v });

  const reset = () => setPolicy({
    ...policy,
    wOutput: 25, wEffort: 20, wQuality: 25, wSpeed: 15, wDiscipline: 15,
    systemWeight: 100, managerWeight: 0,
    expectedMonthlyHours: 176, graceDays: 90,
  });

  const save = async () => {
    if (weightSum !== 100) { toast.error(`Weights must sum to 100 (current: ${weightSum})`); return; }
    setSaving(true);
    try {
      const r = await api.put("/performance/policy", {
        wOutput: Number(policy.wOutput), wEffort: Number(policy.wEffort),
        wQuality: Number(policy.wQuality), wSpeed: Number(policy.wSpeed),
        wDiscipline: Number(policy.wDiscipline),
        systemWeight: 100, managerWeight: 0,
        expectedMonthlyHours: Number(policy.expectedMonthlyHours), graceDays: Number(policy.graceDays),
      });
      setPolicy(r.data);
      toast.success("Policy saved");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const Slider = ({ label, field, icon }: any) => (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">{icon} {label}</label>
        <input
          type="number" min={5} max={50} disabled={!isAdmin}
          value={policy[field]}
          onChange={e => setWeight(field, Math.min(50, Math.max(5, Number(e.target.value))))}
          className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-right"
        />
        <span className="text-xs text-gray-500 w-6">%</span>
      </div>
      <input
        type="range" min={5} max={50} disabled={!isAdmin}
        value={policy[field]} onChange={e => setWeight(field, Number(e.target.value))}
        className="w-full accent-blue-600"
      />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-6">
        <BarChart3 size={22} className="text-blue-600"/>
        <h1 className="text-xl font-semibold text-gray-900">Performance Evaluation Policy</h1>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-4">Global Weights (must sum to 100%)</h2>
        <Slider label="📦 Output (Productivity)" field="wOutput" icon="📦"/>
        <Slider label="⏱ Effort (Time)" field="wEffort" icon="⏱"/>
        <Slider label="⭐ Quality" field="wQuality" icon="⭐"/>
        <Slider label="🏃 Speed" field="wSpeed" icon="🏃"/>
        <Slider label="📋 Discipline" field="wDiscipline" icon="📋"/>
        <div className={`mt-2 text-sm ${weightSum === 100 ? "text-green-600" : "text-red-600"} font-semibold`}>
          Total: {weightSum}% {weightSum === 100 ? "✓" : "— must equal 100"}
        </div>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-4">Settings</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-700">Expected Monthly Hours</label>
            <input type="number" min={120} max={220} disabled={!isAdmin}
              value={policy.expectedMonthlyHours}
              onChange={e => setPolicy({ ...policy, expectedMonthlyHours: Number(e.target.value) })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
            <p className="text-xs text-gray-400 mt-1">Default: 176 (22 days × 8h)</p>
          </div>
          <div>
            <label className="text-sm text-gray-700">Grace Period for New Employees (days)</label>
            <input type="number" min={0} max={180} disabled={!isAdmin}
              value={policy.graceDays}
              onChange={e => setPolicy({ ...policy, graceDays: Number(e.target.value) })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
            <p className="text-xs text-gray-400 mt-1">Days before ranking applies</p>
          </div>
        </div>
      </section>

      {/* Employee Performance Explorer */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-purple-600"/>
          <h2 className="text-sm font-semibold text-gray-800">Employee Performance Explorer</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">Search and view any employee's performance evaluation.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search employee by name..."
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <select
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
          {filteredEmployees.length === 0 ? (
            <div className="p-4 text-sm text-gray-400 text-center">No employees found</div>
          ) : (
            filteredEmployees.slice(0, 50).map(emp => (
              <button
                key={emp.id}
                onClick={() => navigate(`/my-performance?user=${emp.id}`)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {(emp.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{emp.name}</div>
                  <div className="text-xs text-gray-500">
                    {emp.department || "No department"} · {emp.role}
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-400"/>
              </button>
            ))
          )}
        </div>
        {filteredEmployees.length > 50 && (
          <p className="text-xs text-gray-400 mt-2">Showing first 50 — refine search to see specific employees</p>
        )}
      </section>

            {isAdmin && (
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-60">
            <Save size={16}/> {saving ? "Saving..." : "Save Policy"}
          </button>
          <button onClick={reset}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            <RotateCcw size={16}/> Reset to Defaults
          </button>
        </div>
      )}

      {!isAdmin && <p className="text-xs text-gray-400 italic">Read-only — only admins can modify.</p>}
    </div>
  );
}
